import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import isNil from "lodash/isNil";
import { isAvailabilityRule } from "PFCore/helpers/filters/type_checks";
import { useDebounce } from "PFCore/helpers/use_debounce";
import { stringify } from "PFCore/helpers/use_deterministic_stringify";
import { AvailabilityMode, AvailabilityRule, AvailabilityRuleV2, FilterWithDefaults } from "PFTypes";
import { useCallback, useEffect, useMemo, useState } from "react";

const DEBOUNCE_MS = 600;
import { camelizeApiKeys } from "PFCore/api/utils";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { useAvailabilityRequirementsValidation } from "PFCore/hooks/queries/availability/use_availability_requirements_validation";

export type UseAvailabilityStateReturn = {
  availability: Partial<AvailabilityRule>;
  updateAvailability: (newValue: Partial<AvailabilityRule> | null, timeRuleModeChecked?: boolean) => void;
  restoreAvailability: VoidFunction;
  errors: Record<string, string>;
  isValidating: boolean;
};

export const useAvailabilityState = (
  filter: FilterWithDefaults | null,
  onChange: (updated: Partial<AvailabilityRuleV2> | null) => void
): UseAvailabilityStateReturn => {
  const filterValue = useMemo(() => filter?.value || null, [stringify(filter?.value || {})]);
  const filterDefaultValue = useMemo(() => {
    const defaultValue = filter?.defaultValue ?? filter?.default_value;
    return isAvailabilityRule(defaultValue) ? defaultValue : null;
  }, [filter?.defaultValue, filter?.default_value]);

  const { mutateAsync: validateAvailabilityRequirements, isPending: isValidating } =
    useAvailabilityRequirementsValidation();
  const { getSingleErrorByType } = useResponseErrors();

  const [availability, setAvailability] = useState<Partial<AvailabilityRule>>(
    isAvailabilityRule(filterValue) ? filterValue : {}
  );

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setAvailability(isAvailabilityRule(filterValue) ? filterValue : {});
  }, [filterValue]);

  const validateAndPropagate = useDebounce(
    useCallback(
      (availability: AvailabilityRule | null) => {
        const submitUpdatedAvailability = () => {
          setErrors({});
          onChange(availability ? camelizeApiKeys(availability) : null);
        };

        if (availability === null) {
          submitUpdatedAvailability();
          return;
        }

        validateAvailabilityRequirements(availability as unknown as AvailabilityRuleV2)
          .then(submitUpdatedAvailability)
          .catch(({ response }) => {
            setErrors(getSingleErrorByType(response) || {});
          });
      },
      [getSingleErrorByType, onChange]
    ),
    DEBOUNCE_MS
  );

  const updateAvailability = useCallback<UseAvailabilityStateReturn["updateAvailability"]>(
    (newValue, timeRuleModeChecked) => {
      if ((isEmpty(availability) && !newValue) || isEqual(newValue, availability)) {
        return;
      }

      if (!newValue) {
        const mode = !isNil(timeRuleModeChecked)
          ? { mode: timeRuleModeChecked ? AvailabilityMode.TimeRule : AvailabilityMode.Within }
          : {};

        setAvailability(mode);
        validateAndPropagate(newValue);
        return;
      }

      setAvailability(newValue);

      if (newValue.mode) {
        validateAndPropagate({
          mode: newValue.mode,
          availability_threshold: newValue.availability_threshold ?? 0,
          ranges: newValue.ranges || []
        } as AvailabilityRule);
      }
    },
    [availability, validateAndPropagate]
  );

  const restoreAvailability = useCallback(() => {
    if (filterDefaultValue) {
      updateAvailability(filterDefaultValue);
    }
  }, [filterDefaultValue, updateAvailability]);

  return {
    availability,
    updateAvailability,
    restoreAvailability,
    errors,
    isValidating
  };
};
